import React from 'react';
import ComponentName from '../../constant/ComponentName';
import { ChannelUndeployDependent } from '../../domain/stream/transaction/ChannelUndeployDependent';
import LanguageUtil from '../../util/LanguageUtil';
import CustomConfirmDialog from '../custom/CustomConfirmDialog';

interface ChannelUndeployImpactDialogProps {
  visible: boolean;
  dependents: ChannelUndeployDependent[];
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}

const ChannelUndeployImpactDialog = ({ visible, dependents, onCancel, onConfirm }: ChannelUndeployImpactDialogProps) => {
  const items = dependents ?? [];
  return (
    <CustomConfirmDialog
      visible={visible}
      onHide={onCancel}
      icon={<img src={'/icons/error.svg'} alt='error' className='w-[172px] h-[172px] flex items-center justify-center' />}
      title={LanguageUtil.get({
        id: 'messages.confirm.undeployDependentsTitle',
        defaultMessage: 'These will also be undeployed. Are you sure?',
      })}
      description={LanguageUtil.get({
        id: 'messages.confirm.undeployDependentsDescription',
        defaultMessage: 'The deployed components below are bound to this channel and will be undeployed.',
      })}
      body={
        <ul className='flex w-full max-w-md flex-col gap-y-1.5 p-5 text-left text-sm text-f-black-2'>
          {items.map((dependent) => (
            <li key={`${dependent.componentType}:${dependent.name}`}>
              <span className='font-bold'>{resolveLabel(dependent.componentType)}:</span> {dependent.name}
            </li>
          ))}
        </ul>
      }
      cancelLabel={LanguageUtil.get({ id: 'common.label.cancel', defaultMessage: 'Cancel' })}
      confirmLabel={LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeploy' })}
      onConfirm={onConfirm}
    />
  );
};

const resolveLabel = (componentType: string): string => {
  switch (componentType) {
    case ComponentName.STREAM:
      return LanguageUtil.get({ id: 'common.label.store', defaultMessage: 'Store' });
    case ComponentName.VELOCITY:
      return LanguageUtil.get({ id: 'common.label.velocity', defaultMessage: 'Velocity' });
    case ComponentName.QUERY_VARIABLE:
      return LanguageUtil.get({ id: 'common.label.query', defaultMessage: 'Query' });
    case ComponentName.LIST_VARIABLE:
      return LanguageUtil.get({ id: 'common.label.listVariable', defaultMessage: 'List Variable' });
    case ComponentName.API_VARIABLE:
      return LanguageUtil.get({ id: 'common.label.api', defaultMessage: 'Api' });
    case ComponentName.BASIC_VARIABLE:
      return LanguageUtil.get({ id: 'common.label.basic', defaultMessage: 'Basic' });
    case ComponentName.RULE:
      return LanguageUtil.get({ id: 'common.label.rule', defaultMessage: 'Rule' });
    case ComponentName.ACTION:
      return LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' });
    case ComponentName.AI_MODEL:
      return LanguageUtil.get({ id: 'common.label.mlops', defaultMessage: 'MLOps' });
    case ComponentName.TRACKER:
      return LanguageUtil.get({ id: 'common.label.tracker', defaultMessage: 'Tracker' });
    case ComponentName.LIST:
      return LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' });
    case ComponentName.CONNECTION:
      return LanguageUtil.get({ id: 'common.label.connection', defaultMessage: 'Connection' });
    case ComponentName.ENRICHMENT:
      return LanguageUtil.get({ id: 'common.label.enrichment', defaultMessage: 'Enrichment' });
    case ComponentName.TRANSACTION:
      return LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' });
    case ComponentName.POLICY:
      return LanguageUtil.get({ id: 'common.label.policy', defaultMessage: 'Policy' });
    case ComponentName.ASSESSMENT:
      return LanguageUtil.get({ id: 'common.label.assessment', defaultMessage: 'Assessment' });
    default:
      return componentType;
  }
};

export default ChannelUndeployImpactDialog;
